import { history_data } from "@screens/sampleData";
import { getHistoryDateFromFlutter } from "@services/timeHelpers";
import { StatusBlock } from "./Status";
import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { colors } from "sexy";
import { useState } from "react";
import { CustomDocument } from "@components/ui/documents/CustomDocument";
import { EmailSent, Org, Project, ProjectHistoryEvent, SignatureRequest, User } from "types";
import { EmailSentModal } from "@components/modals/EmailSentModal";
import { PhotoModal } from "@components/modals/PhotoModal";
import { DocumentModal } from "@components/modals/DocumentModal";
import { SignatureStatusModal } from "@components/modals/SignatureStatusModal";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { getPhotosByIdsFromProject } from "helpers/projectHelpers";
import { CustomImage } from "@components/ui/images/CustomImage";
import { useDataStore } from "@store/user_org";

const getDocumentsFromProject = (project?: Project) => {
  if (!project?.folders) return [];

  const documents: any[] = [];
  const extractDocs = (folders: any[]) => {
    folders.forEach((folder) => {
      if (folder.documents) {
        documents.push(...folder.documents);
      }
      if (folder.sub_folders) {
        extractDocs(folder.sub_folders);
      }
    });
  };

  extractDocs(project.folders);
  return documents;
};

export const VerticalTimeline = ({
  history_events = history_data,
  activeProject,
  sentEmails = [],
  userData,
  orgData,
  setActiveProject,
  setSentEmails,
  search = "",
  isGuest = false,
}: {
  history_events?: ProjectHistoryEvent[];
  activeProject?: Project;
  sentEmails?: EmailSent[];
  userData?: User;
  orgData?: Org;
  setActiveProject?: (project: Project) => void;
  setSentEmails?: (emails: EmailSent[]) => void;
  search?: string;
  isGuest?: boolean;
}) => {
  const [emailSentOpen, setEmailSentOpen] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [selectedSignatureRequest, setSelectedSignatureRequest] = useState<SignatureRequest | null>(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);
  const orgMembers = useDataStore((state) => state.orgMembers);

  // Resolve current user avatar from orgMembers instead of stale snapshot
  const resolveUser = (eventUser: ProjectHistoryEvent["user"]) => {
    const member = orgMembers.find((m) => m.id === eventUser.id);
    if (member) {
      return {
        photo_url: member.customization?.photo_url,
        avatar_seed: member.customization?.avatar_seed,
        display_name: member.infos?.display_name || eventUser.display_name,
      };
    }
    return { photo_url: eventUser.photo_url, avatar_seed: undefined, display_name: eventUser.display_name };
  };

  const normalizedSearch = search.trim().toLowerCase();
  const allDocuments = getDocumentsFromProject(activeProject);

  // Guests only see emails addressed to a client contact; artisan emails
  // (and any photos attached to them) are hidden.
  const clientContactEmails = new Set(
    (activeProject?.infos?.client?.contacts || [])
      .map((c) => c.email?.trim().toLowerCase())
      .filter((e): e is string => !!e),
  );
  const isEmailHiddenFromGuest = (emailSentId?: string) => {
    if (!isGuest) return false;
    if (!emailSentId) return true;
    const email = sentEmails.find((e) => e.id === emailSentId);
    if (!email) return true;
    if (clientContactEmails.size === 0) return true;
    const recipients = [
      ...(email.emails_to || []),
      ...(email.emails_cc || []),
      ...(email.emails_bcc || []),
    ].map((r) => r.trim().toLowerCase());
    return !recipients.some((r) => clientContactEmails.has(r));
  };

  const seenIds = new Set<string>();
  const filteredEvents = (normalizedSearch
    ? history_events.filter((entry) => {
        const action = entry.action?.toLowerCase() || "";
        const userName = entry.user.display_name?.toLowerCase() || "";
        const controlPoint = entry.control_point?.name?.toLowerCase() || "";
        const emailSubject = entry.email?.subject?.toLowerCase() || "";
        const docNames = allDocuments
          .filter((doc) => entry.documents_id?.includes(doc.id))
          .map((doc) => doc.name?.toLowerCase() || "")
          .join(" ");
        return (
          action.includes(normalizedSearch) ||
          userName.includes(normalizedSearch) ||
          controlPoint.includes(normalizedSearch) ||
          emailSubject.includes(normalizedSearch) ||
          docNames.includes(normalizedSearch)
        );
      })
    : history_events
  ).filter((entry) => {
    // Deduplicate events by ID (guards against ArrayUnion duplicates in Firestore)
    if (entry.id) {
      if (seenIds.has(entry.id)) return false;
      seenIds.add(entry.id);
    }
    // For guests, drop any event whose email is hidden — no trace of the
    // send in the project feed (user/action line, timestamp, nothing).
    if (entry.email && isEmailHiddenFromGuest(entry.email.email_sent_id)) {
      return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col max-h-full overflow-auto">
      {emailSentOpen && (
        <EmailSentModal
          email_sent_id={emailSentOpen}
          onClose={() => setEmailSentOpen(null)}
          sentEmails={sentEmails}
          activeProject={activeProject}
          orgData={orgData}
        />
      )}

      {selectedPhoto && (
        <PhotoModal
          onClose={() => setSelectedPhoto(null)}
          photo_id={selectedPhoto}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
        />
      )}

      {selectedDocument && activeProject && (
        <DocumentModal
          document_id={selectedDocument}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          onClose={() => setSelectedDocument(null)}
          readOnly={isGuest}
          hideSignatures={isGuest}
        />
      )}

      {selectedSignatureRequest && (
        <SignatureStatusModal
          onClose={() => setSelectedSignatureRequest(null)}
          signatureRequest={selectedSignatureRequest}
        />
      )}

      {signedPdfUrl && (
        <PDFViewerModal
          pdfUrl={signedPdfUrl}
          title="Document signé"
          onClose={() => setSignedPdfUrl(null)}
        />
      )}

      {filteredEvents.length === 0 && (
        <div className="flex flex-col items-center text-center py-8 gap-1">
          <div className="text-h2 font-medium text-neutral-700">
            {normalizedSearch ? "Aucun résultat" : "Aucun événement"}
          </div>
          <div className="text-h3 text-neutral-400">
            {normalizedSearch
              ? "Essayez avec d'autres mots-clés."
              : "Les événements du projet apparaîtront ici au fur et à mesure."}
          </div>
        </div>
      )}

      {(() => {
        const sorted = filteredEvents.sort((a, b) => (a.date < b.date ? 1 : -1));

        // Group consecutive control-point-only events within 5 minutes
        const getSeconds = (d: any) => typeof d === "string" ? new Date(d).getTime() / 1000 : d?.seconds || 0;
        const isControlPointOnly = (e: ProjectHistoryEvent) =>
          !!e.control_point?.new_status && !e.documents_id?.length && !e.photos_id?.length && !e.email;

        type Group = { leader: ProjectHistoryEvent; grouped: ProjectHistoryEvent[] };
        const groups: Group[] = [];
        for (const entry of sorted) {
          const last = groups[groups.length - 1];
          if (
            last &&
            isControlPointOnly(entry) &&
            isControlPointOnly(last.leader) &&
            entry.user.id === last.leader.user.id &&
            Math.abs(getSeconds(last.grouped[last.grouped.length - 1]?.date || last.leader.date) - getSeconds(entry.date)) < 300
          ) {
            last.grouped.push(entry);
          } else {
            groups.push({ leader: entry, grouped: [entry] });
          }
        }

        let flatIndex = 0;
        return groups.map((group, gi) => {
          const entry = group.leader;
          const isGrouped = group.grouped.length > 1;
          const currentFlatIndex = flatIndex;
          flatIndex += 1;

          return (
            <div key={currentFlatIndex} className="flex flex-row">
              {/* Date column */}
              <div className="mr-2 text-h3 text-neutral-400 w-24 flex-shrink-0">
                {getHistoryDateFromFlutter(entry.date)}
              </div>

              {/* Timeline dot + line column */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-neutral-400 mt-1"></div>
                {gi < groups.length - 1 && (
                  <div className="w-0.5 bg-neutral-300 flex-1 my-2"></div>
                )}
              </div>

              {/* Content column */}
              <div className="flex flex-col gap-2 ml-6 flex-1 pb-6 max-w-[calc(100%-150px)]">
                <div className="flex flex-row gap-2 items-start">
                  {(() => {
                    const resolved = resolveUser(entry.user);
                    return (
                      <UserAvatar
                        src={resolved.photo_url}
                        value={resolved.avatar_seed || resolved.display_name}
                        size={32}
                        className="w-8 h-8 rounded-full flex-shrink-0 border border-neutral-300 mt-0.5"
                      />
                    );
                  })()}
                  <div className="flex flex-col flex-1">
                    <div className="text-h2 font-semibold h-4.5">
                      {resolveUser(entry.user).display_name}
                    </div>
                    <div className="text-h3 text-neutral-400">{entry.action}</div>
                  </div>
                </div>

                {isGrouped ? (
                  <div className="mt-2 mb-2 flex flex-row flex-wrap gap-1">
                    {group.grouped.map((ge, geIdx) => (
                      ge.control_point?.name && (
                        <StatusBlock
                          key={geIdx}
                          type="success"
                          label={ge.control_point.name}
                        />
                      )
                    ))}
                  </div>
                ) : (
                  entry.control_point?.new_status && (
                    <div className="mt-4 mb-2 flex flex-col gap-1">
                      <StatusBlock
                        type="success"
                        label={entry.control_point.name}
                      />
                    </div>
                  )
                )}

                {entry.documents_id && (
                  <div className="flex flex-col mt-2 gap-0">
                    {getDocumentsFromProject(activeProject)
                      .filter((doc) => entry.documents_id?.includes(doc.id) && (!isGuest || !doc.hidden))
                      .map((doc, docIndex) => (
                        <CustomDocument
                          key={docIndex}
                          name={doc.name}
                          url={doc.file?.url || ""}
                          hidden={doc.hidden}
                          indentLeft={0}
                          fontSize={12}
                          onClick={() => setSelectedDocument(doc.id)}
                        />
                      ))}
                  </div>
                )}

                {getPhotosByIdsFromProject(
                  activeProject,
                  entry.photos_id || [],
                  userData,
                ).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 max-w-full">
                    {getPhotosByIdsFromProject(
                      activeProject,
                      entry.photos_id || [],
                      userData,
                    ).map((photo, photoIndex) => (
                      <CustomImage
                        key={photoIndex}
                        source={{ uri: photo.file.url }}
                        alt={`Photo ${photoIndex + 1}`}
                        className="rounded-xl w-100 h-24 cursor-pointer"
                        hidden={photo.hidden}
                        onClick={() => setSelectedPhoto(photo.id)}
                      />
                    ))}
                  </div>
                )}

                {entry.email && (
                  <div
                    className="mt-3 p-2.5 w-fit bg-neutral-100 rounded-lg border-2 border-neutral-200 cursor-pointer hover:bg-neutral-150 transition-colors"
                    onClick={() => {
                      setEmailSentOpen(entry.email?.email_sent_id || null);
                    }}
                  >
                    <div className="text-h3 text-neutral-400">
                      {entry.email.subject}
                    </div>
                  </div>
                )}

                {entry.signature_request_id && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <div
                      className="p-2.5 w-fit bg-neutral-100 rounded-lg border-2 border-neutral-200 cursor-pointer hover:bg-neutral-150 transition-colors flex items-center gap-2"
                      onClick={async () => {
                        try {
                          const sr = await signatureRequestsApi.get(entry.signature_request_id!);
                          setSelectedSignatureRequest(sr);
                        } catch {
                          // fallback: ignore if request not found
                        }
                      }}
                    >
                      <Icon1 name="sign" size={16} />
                      <div className="text-h3 text-neutral-400">
                        Voir la demande de signature
                      </div>
                    </div>
                    <div
                      className="p-2.5 w-fit bg-neutral-100 rounded-lg border-2 border-neutral-200 cursor-pointer hover:bg-neutral-150 transition-colors flex items-center gap-2"
                      onClick={async () => {
                        try {
                          const sr = await signatureRequestsApi.get(entry.signature_request_id!);
                          if (sr.current_file?.url) {
                            setSignedPdfUrl(sr.current_file.url);
                          }
                        } catch {
                          // ignore
                        }
                      }}
                    >
                      <Icon1 name="pdf" size={16} />
                      <div className="text-h3 text-neutral-400">
                        Voir le document signé
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        });
      })()}
    </div>
  );
};
